<script lang="ts" setup>
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { GiftlyMetadata } from '@consumer/types/serializers'
import { useListeners } from '@shared/vue'
import { truncate } from '@consumer/helpers/string'
import { getTagline, generateDefaultTagline, defaultV3, v3WithImageBg } from '@consumer/logic/giftly'
import { useAutoFitText } from '@consumer/composables/useAutoFitText'
import { giftlyBackgroundUrl } from '@consumer/utils/url'
import { faPaintBrush } from '@fortawesome/pro-regular-svg-icons'
import { maskFromName } from '@consumer/assets/masks'
import { cloneDeep } from '@shared/object'

import '@consumer/styles/giftly_image.scss'
import RivePreview from '@shared/components/Rive/canvas/RivePreview.vue'
import newMiscGiftlyLogoIcon from '@consumer/assets/icons/new-misc/giftly-logo.svg?svgicon'

const {
  createMyOwn, gift, inCustomizer, overrideTagline, animatedRiveUrl, showPlaceBubbles,
} = defineProps<{
  gift: GiftTemplate
  createMyOwn?: boolean
  inCustomizer?: boolean
  overrideTagline?: string
  animatedRiveUrl?: string
  showPlaceBubbles?: boolean
}>()

let localMetadata: Ref<GiftlyMetadata>
let localBgImageUrl: Ref<string | undefined>
const animationLoaded = $ref(false)

if (inCustomizer) {
  localMetadata = shallowRef(cloneDeep(gift.giftlyMetadata) || {})
  localBgImageUrl = ref(gift.giftlyMetadata?.backgroundImageUrl)
}

const metadataRef = toRef(() => inCustomizer ? localMetadata.value : gift.giftlyMetadata || {})

const metadata = $(metadataRef)

const bgImageUrl = $(toRef(() =>
  inCustomizer ? localBgImageUrl.value : metadata.backgroundImageUrl))

const emit = defineEmits<{
  clickBackground: []
  clickItem: []
  imageLoaded: [Event | undefined]
}>()

const isClickable = useListeners(listeners => ({
  background: typeof listeners.onClickBackground === 'function',
  item: typeof listeners.onClickItem === 'function',
} as const))

const designName = $computed(() =>
  metadata.designName
    || (bgImageUrl ? v3WithImageBg : defaultV3.name))

const usesUnsplashImage = $computed(() => designName === v3WithImageBg)

const backgroundColor = $computed(() =>
  metadata.backgroundColor
    || ((designName === defaultV3.name && defaultV3.type !== 'image') && '#44A4FF')
    || undefined)

const backgroundImageUrl = $computed(() => {
  if ((inCustomizer && bgImageUrl) || usesUnsplashImage) return bgImageUrl
  if (backgroundColor) return undefined
  return giftlyBackgroundUrl(designName)!
})

const tagline = $computed(() => overrideTagline ?? (getTagline(gift) || generateDefaultTagline(gift)))
const taglineSpan = $ref<HTMLElement>()
const taglineFontSize = useAutoFitText($$(taglineSpan), () => tagline)
const maskColor = $computed(() => createMyOwn ? '#F3F9FF' : metadata.maskColor)
const maskName = $computed(() => metadata.maskName || 'giftly-v3-mask-1')
const logoColor = $computed(() => {
  if (createMyOwn) return '#7BBEFF80'
  return metadata.logoColor || (designName === defaultV3.name ? '#44a4ff' : '#FFF')
})
const maskSvg = $computed(() => maskName && maskFromName(maskName))
const isAnimated = $computed(() => !!animatedRiveUrl)

// Mirrors GiftCards::MultiplaceGiftPlacesIcons#place_images — the denormalized
// column first, then the places the full serializers send, then a single place.
const placeIconUrls = $computed<(string | undefined)[]>(() => {
  if (!showPlaceBubbles) return []
  if (gift.placeIconUrls?.length) return gift.placeIconUrls
  if (gift.places?.length) return gift.places.map(place => place.imageUrl).filter(Boolean) as string[]

  // A single-place gift keeps its circle even with no photo; `address` is what
  // separates one from a universal gift idea, same test placeCardProps uses.
  return 'address' in gift ? [gift.imageUrl] : []
})

let crossorigin = $ref<'anonymous'>()
watch(() => backgroundImageUrl, (backgroundImageUrl) => {
  crossorigin = inCustomizer && gift.giftlyMetadata?.designName === v3WithImageBg
    ? 'anonymous'
    : undefined
}, { immediate: true })

if (inCustomizer) {
  // In order to avoid flicker when switching between designs with backgrounds,
  // change the image so that it can load first, then once loaded apply the rest
  // of the style.
  watch(() => gift, (gift) => {
    const newImageUrl = gift.giftlyMetadata?.backgroundImageUrl
    if (newImageUrl && newImageUrl !== bgImageUrl)
      localBgImageUrl.value = newImageUrl
    else
      displayNow(gift)
  }, { deep: true })
}

function displayNow (gift: GiftTemplate) {
  localMetadata.value = cloneDeep(gift.giftlyMetadata) || {}
  localBgImageUrl.value = metadata.backgroundImageUrl
}

function onImageLoaded (event?: Event) {
  if (inCustomizer) displayNow(gift)
  emit('imageLoaded', event)
}

// eslint-disable-next-line
const transparentSrc = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII='
</script>

<template>
  <div
    class="giftly-image-v3-wrapper giftly-image-wrapper select-none"
  >
    <div
      class="giftly-image-v3"
      :class="usesUnsplashImage && `with-${maskName}`"
      :style="{
        background: (createMyOwn && '#7bbeff33') || backgroundColor
      }"
    >

      <div v-if="!createMyOwn && isAnimated && !isClickable.background" class="absolute w-full h-full">
        <RivePreview
          :riveUrl="animatedRiveUrl!"
          stateMachine="mainSM"
          artboard="mainArtboard"
          class="w-full h-full"
          :fallbackImageUrl="backgroundImageUrl"
          @loaded="() => {
            animationLoaded = true
            onImageLoaded()
          }"
        />
      </div>

      <img
        v-else-if="!createMyOwn"
        :key="crossorigin"
        :src="backgroundImageUrl || transparentSrc"
        class="giftly-image-v3--background-image"
        :class="isClickable.background && 'clickable'"
        name="nc_giftly_background"
        :loading="inCustomizer ? undefined : 'lazy'"
        :crossorigin="crossorigin"
        :alt="`Background for ${tagline} Gift Card`"
        :draggable="false"
        @click="emit('clickBackground')"
        @load="onImageLoaded"
        @error="crossorigin = undefined"
      >

      <div
        v-if="!isAnimated"
        class="giftly-image-v3--mask pointer-events-none"
        :style="{ color: maskColor || '#00407D' }"
        v-html="maskSvg"
      />

      <i
        v-if="!gift.noTexts"
        class="giftly-image-v3--giftly-logo"
        :style="{ color: logoColor }"
      >
        <GIcon :svg="newMiscGiftlyLogoIcon" class="text-inherit" size=""/>
      </i>

      <div
        v-if="createMyOwn"
        class="flex items-center justify-center absolute inset-0 text-primary-base"
      >
        <div
          class="bg-white shadow-md rounded-full flex items-center justify-center"
          style=" height: 80em;width: 80em;"
        >
          <GIcon :icon="faPaintBrush" style="height: 32em; width: 32em;"/>
        </div>

        <span class="absolute font-semibold text-center" style="bottom: 2em; font-size: 18em;">
          Create your own
        </span>
      </div>
      <div v-else-if="!gift.noTexts" class="giftly-image-v3--tagline">
        <div
          class="giftly-image-v3--tagline--text"
          :class="{ clickable: isClickable.item }"
          :style="{ fontSize: `${taglineFontSize}em` }"
          @click="emit('clickItem')"
        >
          <span ref="taglineSpan">{{ truncate(tagline, { length: 66 }) }}</span>
        </div>
      </div>

      <GiftlyPlaceIcons :iconUrls="placeIconUrls"/>
    </div>
  </div>
</template>
